import { useState, MouseEvent } from 'react';
import {
  Box,
  MenuItem,
  Skeleton,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import { Iconify, Label } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { TableMoreMenu, TableRowLink } from '@/components/table';
import { ButtonColors } from '@/types';
import { fDateTime } from '@/utils/formatTime';
import { inAppPopupsPath } from '../routes/paths';
import { InAppPopup, InAppPopupStats } from '../types';
import {
  formatInAppPopupStatsCount,
  formatInAppPopupTapRate,
  getPopupActionLabel,
  getPopupPlatformLabel,
} from '../utils';

type PopupDialog = {
  name: string;
  title: string;
  text: string;
  loadingButtonText: string;
  loadingButtonColor?: ButtonColors;
  agreeFunction: VoidFunction;
};

type InAppPopupTableRowProps = {
  row: InAppPopup;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  stats?: InAppPopupStats;
  statsLoading?: boolean;
  onDuplicateRow: VoidFunction;
  onEditRow: VoidFunction;
  onViewStats: VoidFunction;
  onDeleteRow: VoidFunction;
  onToggleActive: (active: boolean) => void;
};

const StatsSummaryCell = ({
  loading,
  stats,
}: {
  loading?: boolean;
  stats?: InAppPopupStats;
}) => {
  const items = [
    {
      label: 'Views',
      value: formatInAppPopupStatsCount(stats?.impressions),
    },
    {
      label: 'Taps',
      value: formatInAppPopupStatsCount(stats?.button_taps),
    },
    {
      label: 'Dismiss',
      value: formatInAppPopupStatsCount(stats?.dismissals),
    },
    {
      label: 'Users',
      value: formatInAppPopupStatsCount(stats?.action_users),
    },
    {
      label: 'Rate',
      value: formatInAppPopupTapRate(stats),
    },
  ];

  return (
    <TableCell align="left" sx={{ minWidth: 210 }}>
      {loading ? (
        <Skeleton width={180} />
      ) : (
        <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
          {items.map((item) => (
            <Box key={item.label} sx={{ minWidth: 32 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', lineHeight: 1.2 }}
              >
                {item.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}
    </TableCell>
  );
};

export default function InAppPopupTableRow({
  row,
  canCreate,
  canUpdate,
  canDelete,
  stats,
  statsLoading,
  onDuplicateRow,
  onEditRow,
  onViewStats,
  onDeleteRow,
  onToggleActive,
}: InAppPopupTableRowProps) {
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [selectedDialog, setSelectedDialog] = useState<PopupDialog | null>(
    null
  );

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const platformLabels = row.platforms?.length
    ? row.platforms.map(getPopupPlatformLabel)
    : ['All platforms'];

  const dialogs: PopupDialog[] = [
    {
      name: 'delete',
      title: 'Delete popup',
      text: `Are you sure you want to delete ${row.id}?`,
      loadingButtonText: 'Delete',
      loadingButtonColor: 'error',
      agreeFunction: onDeleteRow,
    },
    {
      name: 'deactivate',
      title: 'Deactivate popup',
      text: `Are you sure you want to deactivate ${row.id}?`,
      loadingButtonText: 'Deactivate',
      agreeFunction: () => onToggleActive(false),
    },
    {
      name: 'reactivate',
      title: 'Reactivate popup',
      text: `Are you sure you want to reactivate ${row.id}?`,
      loadingButtonText: 'Reactivate',
      agreeFunction: () => onToggleActive(true),
    },
  ];

  const handleOpenDialog = (dialogName: string) => {
    const nextDialog = dialogs.find((dialog) => dialog.name === dialogName);
    if (nextDialog) {
      setSelectedDialog(nextDialog);
    }
  };

  const runDialogAction = () => {
    selectedDialog?.agreeFunction();
    setSelectedDialog(null);
  };

  return (
    <>
      {selectedDialog && (
        <DialogSimple
          dialogTitle={selectedDialog.title}
          open={!!selectedDialog}
          onClickButtonClose={() => setSelectedDialog(null)}
          onClickButtonLoading={runDialogAction}
          loadingButtonText={selectedDialog.loadingButtonText}
          loadingButtonColor={selectedDialog.loadingButtonColor ?? 'primary'}
          disagreeButtonText="Cancel"
          disagreeButtonColor="inherit"
        >
          <Box sx={{ px: 3, py: 1.5 }}>{selectedDialog.text}</Box>
        </DialogSimple>
      )}

      <TableRow hover>
        <TableCell align="left">
          <TableRowLink
            linkText={row.id}
            linkTo={inAppPopupsPath('edit', { popupId: row.id })}
            sx={{ width: 'fit-content' }}
          />
        </TableCell>

        <TableCell align="left" sx={{ maxWidth: 240 }}>
          <Typography variant="subtitle2" noWrap>
            {row.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            {row.body}
          </Typography>
        </TableCell>

        <TableCell align="left">
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {platformLabels.map((platform) => (
              <Label color="info" key={platform}>
                {platform}
              </Label>
            ))}
          </Stack>
        </TableCell>

        <TableCell align="left">{row.priority}</TableCell>

        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
          {fDateTime(row.starts_at)}
        </TableCell>

        <TableCell align="left" sx={{ whiteSpace: 'nowrap' }}>
          {fDateTime(row.expires_at)}
        </TableCell>

        <TableCell align="left">
          <Stack spacing={0.5}>
            {row.buttons?.length ? (
              row.buttons.map((button, index) => (
                <Box key={`${button.label}-${index}`}>
                  <Typography variant="body2" noWrap>
                    {button.label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {getPopupActionLabel(button)}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No buttons
              </Typography>
            )}
          </Stack>
        </TableCell>

        <StatsSummaryCell loading={statsLoading} stats={stats} />

        <TableCell align="left">
          <Label color={row.guest_eligible ? 'success' : 'default'}>
            {row.guest_eligible ? 'Yes' : 'No'}
          </Label>
        </TableCell>

        <TableCell align="left">
          <Label color={row.active ? 'success' : 'error'}>
            {row.active ? 'Active' : 'Inactive'}
          </Label>
        </TableCell>

        <TableCell align="right">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            arrow="top-right"
            actions={
              <>
                <MenuItem
                  onClick={() => {
                    onViewStats();
                    handleCloseMenu();
                  }}
                >
                  <Iconify icon={'eva:bar-chart-2-fill'} />
                  View stats
                </MenuItem>
                {canCreate && (
                  <MenuItem
                    onClick={() => {
                      onDuplicateRow();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:copy-fill'} />
                    Duplicate
                  </MenuItem>
                )}
                {canUpdate && (
                  <MenuItem
                    onClick={() => {
                      onEditRow();
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:edit-fill'} />
                    Edit
                  </MenuItem>
                )}
                {canUpdate && row.active && (
                  <MenuItem
                    onClick={() => {
                      handleOpenDialog('deactivate');
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:pause-circle-outline'} />
                    Deactivate
                  </MenuItem>
                )}
                {canUpdate && !row.active && (
                  <MenuItem
                    onClick={() => {
                      handleOpenDialog('reactivate');
                      handleCloseMenu();
                    }}
                  >
                    <Iconify icon={'eva:refresh-outline'} />
                    Reactivate
                  </MenuItem>
                )}
                {canDelete && (
                  <MenuItem
                    onClick={() => {
                      handleOpenDialog('delete');
                      handleCloseMenu();
                    }}
                    sx={{ color: 'error.main' }}
                  >
                    <Iconify icon={'eva:trash-2-outline'} />
                    Delete
                  </MenuItem>
                )}
              </>
            }
          />
        </TableCell>
      </TableRow>
    </>
  );
}
